import { AfterViewInit, Component, HostListener, NgZone, OnDestroy, OnInit } from '@angular/core';
import { COLOR_PICKER_MORE_TOOL } from '../../../../data/product/color-pickers.data';
import { CVD_ABOUT } from '../../../../data/tool/tool-about.data';
import { NAVIGATIONS } from '../../../../data/navigation/navigation.data';
import { THEMES } from '../../../../data/product/product.data';
import { CvdState } from '../../../../component/cvd-widget/cvd-widget.component';
import { AnalyticsService } from '../../../../service/analytics/analytics.service';
import { SeoService } from '../../../../service/seo/seo.service';
import { CVD_TYPES, CvdType, randomThemeScreenshot, simulateCvd } from '../../../../util/cvd-image';

/** One condition's small picture in the All Simulations grid. */
interface CvdThumb {
  type: CvdType;
  name: string;
  note: string;
  share: string;
  url: string;
}

/**
 * /tools/color-blindness-simulator - the colour blindness simulator (2026-10-15). The card (app-cvd-widget) opens on
 * a random Codigrate theme's IDE screenshot, takes any picture uploaded, dropped or pasted, and shows it split,
 * original against the chosen colour vision deficiency; the page then shows the picture through all eight
 * conditions at once. Everything is drawn in the browser; nothing is uploaded.
 */
@Component( {
  selector: 'app-cvd-page',
  templateUrl: './cvd-page.component.html',
  styleUrls: [
    './cvd-page.component.scss',
    '../wallpaper-page/wallpaper-page.component.scss',
    '../tools-page/tools-page.component.scss',
    '../../plugin-pages/plugins-page/plugins-page.component.scss',
  ],
} )
export class CvdPageComponent implements OnInit, AfterViewInit, OnDestroy {

  /** the picture the card opens on */
  startSrc = '';
  startTheme = '';
  current: CvdState | null = null;
  thumbs: CvdThumb[] = [];

  pageSections: { key: string; label: string }[] = [
    { key: 'all', label: 'All Simulations' },
    { key: 'more', label: 'More Color Tools' },
    { key: 'about', label: 'How It Works' },
    { key: 'reviews', label: 'Reviews' },
  ];
  /** More Color Tools: the Color Picker */
  readonly colorPicker = COLOR_PICKER_MORE_TOOL;
  readonly moreToolsSource = 'color-blindness-simulator';
  activeSection = 'top';
  readonly aboutLead = CVD_ABOUT.lead;
  readonly aboutParagraphs = CVD_ABOUT.paragraphs;
  readonly faq = CVD_ABOUT.faq;

  /** the simulation enlarged in the lightbox (its index in thumbs), null when closed */
  lightboxIndex: number | null = null;
  /** that simulation at the picture's full size, drawn when the lightbox opens */
  lightboxUrl = '';

  private sectionObserver?: IntersectionObserver;
  private thumbTimer: any = null;
  private loggedUpload = false;

  constructor(
    private analyticsService: AnalyticsService,
    private seoService: SeoService,
    private zone: NgZone,
  ) {
  }

  ngOnInit(): void {
    this.seoService.setMetaData( {
      title: NAVIGATIONS.colorBlindnessSimulator.title,
      description: NAVIGATIONS.colorBlindnessSimulator.description,
      keywords: NAVIGATIONS.colorBlindnessSimulator.keywords,
      robots: [ 'index', 'follow', 'max-image-preview:large', 'max-snippet:-1' ],
    } );
    const products = ( [] as any[] ).concat( ...THEMES.map( line => line.products ) );
    const shot = randomThemeScreenshot( products );
    if ( shot ) {
      this.startSrc = shot.src;
      this.startTheme = shot.theme;
    }
  }

  ngAfterViewInit(): void {
    if ( typeof IntersectionObserver === 'undefined' ) {
      return;
    }
    this.sectionObserver = new IntersectionObserver( entries => {
      for ( const entry of entries ) {
        if ( entry.isIntersecting ) {
          const sec = ( entry.target as HTMLElement ).dataset[ 'section' ];
          if ( sec && sec !== this.activeSection ) {
            this.zone.run( () => ( this.activeSection = sec ) );
          }
        }
      }
    }, { rootMargin: '-45% 0px -50% 0px', threshold: 0 } );
    setTimeout( () => {
      document.querySelectorAll( '[data-section]' ).forEach( el => this.sectionObserver!.observe( el ) );
    } );
  }

  ngOnDestroy(): void {
    clearTimeout( this.thumbTimer );
    this.sectionObserver?.disconnect();
  }

  get subtitle(): string {
    if ( !this.current ) {
      return 'See any picture as people with color blindness do';
    }
    const name = CVD_TYPES.find( t => t.type === this.current!.type )?.name || '';
    const what = this.current.name === 'codigrate-theme' && this.startTheme ? `The ${ this.startTheme } theme` : 'Your picture';
    return `${ what } with ${ name }`;
  }

  onState( state: CvdState ): void {
    const pictureChanged = !this.current || this.current.base !== state.base;
    this.current = state;
    if ( pictureChanged ) {
      clearTimeout( this.thumbTimer );
      this.thumbTimer = setTimeout( () => this.zone.run( () => this.buildThumbs() ), 60 );
      if ( state.name !== 'codigrate-theme' && !this.loggedUpload ) {
        this.loggedUpload = true;
        this.analyticsService.logEvent( 'Color Blindness Image Uploaded', { width: state.base?.width, height: state.base?.height } );
      }
    }
  }

  /** the picture through all eight conditions, small (at most 520 px wide) */
  private buildThumbs(): void {
    const base = this.current?.base;
    if ( !base ) {
      this.thumbs = [];
      return;
    }
    const full = document.createElement( 'canvas' );
    full.width = base.width;
    full.height = base.height;
    full.getContext( '2d' )?.putImageData( base, 0, 0 );
    const scale = Math.min( 1, 520 / base.width );
    const small = document.createElement( 'canvas' );
    small.width = Math.max( 1, Math.round( base.width * scale ) );
    small.height = Math.max( 1, Math.round( base.height * scale ) );
    const ctx = small.getContext( '2d', { willReadFrequently: true } );
    if ( !ctx ) {
      return;
    }
    ctx.drawImage( full, 0, 0, small.width, small.height );
    const smallData = ctx.getImageData( 0, 0, small.width, small.height );
    const out = document.createElement( 'canvas' );
    out.width = small.width;
    out.height = small.height;
    const octx = out.getContext( '2d' );
    this.thumbs = CVD_TYPES.map( t => {
      octx?.putImageData( simulateCvd( smallData, t.type ), 0, 0 );
      return { type: t.type, name: t.name, note: t.note, share: t.share, url: out.toDataURL( 'image/webp', 0.85 ) };
    } );
  }

  // ---------------------------------------------------------------- the lightbox

  openLightbox( index: number ): void {
    this.lightboxIndex = index;
    this.drawLightbox();
    setTimeout( () => document.querySelector<HTMLElement>( '.cp-lightbox-caption-row .cp-lb-close' )?.focus() );
  }

  closeLightbox(): void {
    this.lightboxIndex = null;
    this.lightboxUrl = '';
  }

  stepLightbox( dir: number ): void {
    if ( this.lightboxIndex === null || !this.thumbs.length ) {
      return;
    }
    const count = this.thumbs.length;
    this.lightboxIndex = ( ( this.lightboxIndex + dir ) % count + count ) % count;
    this.drawLightbox();
  }

  @HostListener( 'document:keydown.arrowleft' )
  onArrowLeft(): void {
    this.stepLightbox( -1 );
  }

  @HostListener( 'document:keydown.arrowright' )
  onArrowRight(): void {
    this.stepLightbox( 1 );
  }

  @HostListener( 'document:keydown.escape' )
  onEscape(): void {
    if ( this.lightboxIndex !== null ) {
      this.closeLightbox();
    }
  }

  /** the open simulation at full size (the small one shows until it is drawn) */
  private drawLightbox(): void {
    const base = this.current?.base;
    const index = this.lightboxIndex;
    this.lightboxUrl = '';
    if ( !base || index === null ) {
      return;
    }
    setTimeout( () => {
      if ( this.lightboxIndex !== index ) {
        return;
      }
      const canvas = document.createElement( 'canvas' );
      canvas.width = base.width;
      canvas.height = base.height;
      canvas.getContext( '2d' )?.putImageData( simulateCvd( base, this.thumbs[ index ].type ), 0, 0 );
      this.zone.run( () => ( this.lightboxUrl = canvas.toDataURL( 'image/webp', 0.9 ) ) );
    } );
  }

  // ---------------------------------------------------------------- the chips

  scrollToSection( key: string ): void {
    const scroller = ( document.body.scrollHeight > document.body.clientHeight + 4 )
      ? document.body
      : document.documentElement;
    if ( key === 'top' ) {
      this.activeSection = 'top';
      this.tweenScroll( scroller, 0 );
      return;
    }
    const el = document.getElementById( 'sec-' + key );
    if ( !el ) {
      return;
    }
    this.activeSection = key;
    let headerOffset = 84;
    const bar = document.querySelector( '.cp-chipbar' ) as HTMLElement | null;
    if ( bar ) {
      const stickyTop = parseFloat( getComputedStyle( bar ).top ) || 72;
      headerOffset = stickyTop + bar.offsetHeight + 16;
    }
    const target = Math.max( 0, el.getBoundingClientRect().top + scroller.scrollTop - headerOffset );
    this.tweenScroll( scroller, target );
  }

  private tweenScroll( scroller: HTMLElement, target: number ): void {
    const startTop = scroller.scrollTop;
    const duration = 420;
    const start = performance.now();
    const ease = ( t: number ) => 1 - Math.pow( 1 - t, 3 );
    const step = ( now: number ) => {
      const p = Math.min( 1, ( now - start ) / duration );
      scroller.scrollTop = startTop + ( target - startTop ) * ease( p );
      if ( p < 1 ) {
        requestAnimationFrame( step );
      }
    };
    requestAnimationFrame( step );
  }
}
